import * as angular from 'angular';

import { groupIdentifierSeparator, guidEmpty, guidGroupValidationPageLink, popoverItem, getUnitName } from '../../common';
import { IValueViewModel, ObjectType, Dashboard, Dictionary, PopoverItemData } from '../../models';
import { FlowChartSeries } from '../../widgets';
import { IdentiferOverlay, SeriesMappingResult } from './';
import { mapConfiguration } from '../../common/helpers/valueViewModelHelper';
import { IDashboardHelperService } from '../../services';
import { ISeriesBuilder, ISeriesBuilderArguments } from '../../services/widgets/ISeriesBuilder';
import { getPopoverCallback } from '../../widgets/common/callback';

export interface IFlowChartService extends ISeriesBuilder<IdentiferOverlay> {
    createOverlay(
        d: FlowChartSeries,
        vm: IValueViewModel,
        dashboards: Dictionary<Dashboard>,
        $scope: angular.IScope,
        isEditing: boolean
    ): IdentiferOverlay;
}

export class FlowChartService implements IFlowChartService {
    static serviceId = 'flowChartService';
    static $inject = [
        '$q',
        '$state',
        '$translate',
        'dashboardHelperService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _$state: angular.ui.IStateService,
        private _$translate: angular.translate.ITranslateService,
        private _dashboardHelperService: IDashboardHelperService,
    ) {
    }

    buildSeriesFromViewModels(args: ISeriesBuilderArguments): angular.IPromise<SeriesMappingResult<IdentiferOverlay>> {
        const { configuration, viewModels, map, errors, dashboards } = args;
        const seriesConfig: FlowChartSeries[] = (configuration as any).series;
        const mapped = mapConfiguration(seriesConfig, viewModels, map, errors);

        const series = mapped.series
            .filter(s => !s.missing)
            .map(s => this.createOverlay(s.configuration as FlowChartSeries, s.viewModel, dashboards));

        return this._$q.resolve({
            error: mapped.error,
            series,
            map
        });
    }

    createOverlay(
        d: FlowChartSeries,
        vm: IValueViewModel,
        dashboards: Dictionary<Dashboard>,
        $scope: angular.IScope = null,
        isEditing: boolean = false
    ): IdentiferOverlay {
        const guid = vm.guid;
        const name = this.getName(d, vm);
        const showPopover = (d.showQuality || d.showValue || d.showUnit);
        const unitName = getUnitName(vm.unitName, d.value);
        const itemData: PopoverItemData = {
            guid: vm.guid,
            name: vm.name,
            identifierType: vm.identifierType,
            variableTypeName: vm.variableTypeName,
            groupUniqueName: vm.groupUniqueName,
            groupList: vm.groupList,
            comment: vm.shortComment
                ? vm.shortComment
                : vm.longComment,
            shortComment: vm.shortComment,
            longComment: vm.longComment
        };
        const result: IdentiferOverlay = {
            name: name,
            borderColor: d.borderColor,
            textColor: d.textColor,
            backgroundColor: d.backgroundColor,
            attributes: vm && showPopover
                ? {
                    'compile': 'wt-popover-svg',
                    'wt-popover-svg': 'true',
                    item: popoverItem(itemData),
                    template: vm.objectType === ObjectType.Tag
                        ? 'tagPopover.html'
                        : 'identifierPopover.html'
                }
                : {},
            position: d.position,
            size: d.size,
            click: isEditing ? undefined : this.getCallback(vm, dashboards),
            dragend: () => {
                if ($scope) {
                    $scope.$apply();
                }
            },
            selected: d.selected && isEditing,
            guid: guid,
            showQuality: d.showQuality,
            showValue: d.showValue,
            showUnit: d.showUnit,
            unitName: unitName,
            valueType: d.value,
            showIcon: d.showIcon,
            icon: (d.dashboardGuid === guidGroupValidationPageLink)
                ? '\uf046'
                : '\uf009',
            dashboardGuid: d.dashboardGuid,
            dashboardName: (d.dashboardGuid === guidGroupValidationPageLink)
                ? this._$translate.instant('Group validation page')
                : dashboards[d.dashboardGuid]?.name
        };
        return result;
    }

    private getCallback(
        vm: IValueViewModel,
        dashboards: Dictionary<Dashboard>
    ): (d: IdentiferOverlay) => void {
        const identifierCallback = getPopoverCallback(vm, this._$state.go);

        return (d: IdentiferOverlay, iconClick?: boolean) => {
            if (iconClick && d.dashboardGuid && d.dashboardGuid !== guidEmpty) {
                const groupGuid = vm.groupList.length > 0 ? vm.groupList[0].guid : undefined;
                const selectedGuid = this._dashboardHelperService.getSelectedGroupGuid(groupGuid);
                const go = this._dashboardHelperService.getGotoDestination(d.dashboardGuid, dashboards, selectedGuid);
                this._$state.go(go.route, go.params);
            }
            else {
                // asked by #19946, do not redirect to identifier if not "visible"
                if (d.showQuality || d.showValue || d.showUnit) {
                    return identifierCallback();
                }
            }
        };
    }

    private getName(d: FlowChartSeries, ivalueViewModel: IValueViewModel): string {
        let name = '';
        if (d.showGroup && ivalueViewModel.groupUniqueName) {
            name += ivalueViewModel.groupUniqueName;
        }
        if (d.showName) {
            if (name) {
                name = `${name}${groupIdentifierSeparator}${ivalueViewModel.name}`;
            } else {
                name = ivalueViewModel.name;
            }
        }
        return name;
    }
}